import { useEffect, useRef } from 'react';
import { useMundialMetrics } from '../useMundialMetrics';
import {
  SCROLL_THRESHOLDS,
  ALWAYS_RESET_SCROLL_SELECTORS,
} from '../useMundialMetrics/constants';

/**
 * Detecta cuando el scroll vertical cruza los thresholds 25/50/75/100% al ir bajando
 * y dispara trackScroll. Cada threshold se dispara UNA SOLA VEZ por sesion de la vista.
 * Los thresholds se REINICIAN cuando cambian las dependencias
 * (contentSubsection, moduloName, pageTitleSegments).
 *
 * @param {Object} params
 * @param {string[]}         params.pageTitleSegments
 * @param {string}           params.contentSubsection
 * @param {string}           params.moduloName
 * @param {boolean}          [params.enabled=true]
 * @param {React.RefObject}  [params.targetRef]
 *        Override explicito. Si se pasa y .current existe, gana sobre scrollSelectors.
 * @param {string[]}         [params.scrollSelectors=ALWAYS_RESET_SCROLL_SELECTORS]
 *        Lista de selectores CSS en orden de prioridad. El tracker observa
 *        el primer match encontrado en el DOM.
 * @param {Function|null}    [params.getVisibleContentIds]
 *        Funcion que retorna un array de strings con los IDs visibles al momento
 *        de disparar el threshold. Solo se usa en Fixtures.
 */
export const useMundialScrollTracker = ({
  pageTitleSegments = [],
  contentSubsection = '',
  moduloName = '',
  enabled = true,
  targetRef = null,
  scrollSelectors = ALWAYS_RESET_SCROLL_SELECTORS,
  getVisibleContentIds = null,
} = {}) => {
  const { trackScroll } = useMundialMetrics();

  const trackScrollRef = useRef(trackScroll);
  trackScrollRef.current = trackScroll;

  const firedThresholds = useRef(new Set());
  const segmentsKey = (pageTitleSegments || []).join('|');
  const selectorsKey = (scrollSelectors || []).join('|');

  useEffect(() => {
    if (!enabled) return undefined;

    firedThresholds.current = new Set();

    // 1. Resolver target: ref explícito > primer selector con match.
    let target = targetRef?.current || null;
    if (!target) {
      for (let i = 0; i < scrollSelectors.length; i += 1) {
        const found = document.querySelector(scrollSelectors[i]);
        if (found) {
          target = found;
          break;
        }
      }
    }

    if (!target) return undefined;

    const isWindow = target === window;

    const getScrollPercentage = () => {
      if (isWindow) {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight <= 0) return null;
        return Math.min(100, Math.round((scrollTop / docHeight) * 100));
      }
      const max = target.scrollHeight - target.clientHeight;
      if (max <= 0) return null;
      return Math.min(100, Math.round((target.scrollTop / max) * 100));
    };

    const handleScroll = () => {
      const pct = getScrollPercentage();
      if (pct === null) return;

      SCROLL_THRESHOLDS.forEach((threshold) => {
        if (pct >= threshold && !firedThresholds.current.has(threshold)) {
          firedThresholds.current.add(threshold);

          const visibleContentIds = typeof getVisibleContentIds === 'function'
            ? getVisibleContentIds()
            : null;

          trackScrollRef.current({
            pageTitleSegments,
            contentSubsection,
            moduloName,
            scrollPercentage: threshold,
            visibleContentIds,
          });
        }
      });
    };

    target.addEventListener('scroll', handleScroll, { passive: true });
    // En Android WebView el evento scroll no llega al DOM porque el sistema operativo
    // gestiona el scroll a nivel nativo. touchend sí propaga al DOM y permite leer
    // scrollTop para detectar los thresholds.
    document.addEventListener('touchend', handleScroll, { passive: true });

    return () => {
      target.removeEventListener('scroll', handleScroll);
      document.removeEventListener('touchend', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, contentSubsection, moduloName, segmentsKey, selectorsKey, targetRef]);
};
